import React, { useEffect, useState, useMemo } from 'react';

import ConfigMenu from '@components/ConfigMenu';
import DropdownChip, { DropdownOption } from '@components/DropdownChip';
import { ProviderRegistry } from '@config/providers/provider.registry';
import { DEFAULT_CHAT_CONFIG } from '@constants/chat';
import AnthropicIcon from '@icon/AnthropicIcon';
import OpenAIIcon from '@icon/OpenAIIcon';
import SettingIcon from '@icon/SettingIcon';
import useStore from '@store/store';
import { ChatInterface, ChatConfig } from '@type/chat';
import { ProviderKey } from '@type/provider';
import { useTranslation } from 'react-i18next';
import { shallow } from 'zustand/shallow';

// Provider icon mapping
const providerIcons: Record<ProviderKey, React.ReactNode> = {
  openai: <OpenAIIcon className="w-4 h-4" />,
  anthropic: <AnthropicIcon className="w-4 h-4" />,
  deepinfra: null, // No icon for deepinfra yet
};

const ChatTitle = React.memo(() => {
  const { t } = useTranslation('model');
  const config = useStore(
    (state) => state.chats?.[state.currentChatIndex]?.config,
    shallow
  );
  const setChats = useStore((state) => state.setChats);
  const currentChatIndex = useStore((state) => state.currentChatIndex);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  // Prepare provider options
  const providerOptions: DropdownOption<ProviderKey>[] = useMemo(() => {
    const availableProviders = ProviderRegistry.getAvailableProviders();
    return availableProviders.map((key: ProviderKey) => ({
      value: key,
      label: ProviderRegistry.getProvider(key).name,
      icon: providerIcons[key],
    }));
  }, []);

  // Prepare model options based on current provider
  const modelOptions: DropdownOption[] = useMemo(() => {
    if (!config?.provider) return [];
    const providerConfig = ProviderRegistry.getProviderConfig(config.provider);
    return providerConfig.models.map((model: { id: string; name: string }) => ({
      value: model.id,
      label: model.name || model.id,
    }));
  }, [config?.provider]);

  const setConfig = (config: ChatConfig) => {
    const updatedChats: ChatInterface[] = JSON.parse(
      JSON.stringify(useStore.getState().chats)
    );
    if (!updatedChats[currentChatIndex]) {
      return;
    }
    updatedChats[currentChatIndex].config = config;
    setChats(updatedChats);
  };

  // Handle provider change
  const handleProviderChange = (newProvider: ProviderKey) => {
    if (!config) return;

    // Get default model for new provider
    const defaultModel = ProviderRegistry.getProviderConfig(newProvider).defaultModel;

    // Update config with new provider and default model
    setConfig({
      ...config,
      provider: newProvider,
      modelConfig: {
        ...config.modelConfig,
        model: defaultModel,
      },
    });
  };

  // Handle model change
  const handleModelChange = (newModel: string) => {
    if (!config) return;

    setConfig({
      ...config,
      modelConfig: {
        ...config.modelConfig,
        model: newModel,
      },
    });
  };

  useEffect(() => {
    const chats = useStore.getState().chats;
    if (chats && chats.length > 0 && currentChatIndex !== -1 && !config) {
      const updatedChats: ChatInterface[] = JSON.parse(JSON.stringify(chats));
      if (!updatedChats[currentChatIndex]) {
        return;
      }
      updatedChats[currentChatIndex].config = {
        provider: DEFAULT_CHAT_CONFIG.provider,
        modelConfig: {
          ...DEFAULT_CHAT_CONFIG.modelConfig,
        },
      };
      setChats(updatedChats);
    }
  }, [config, currentChatIndex, setChats]);

  return config ? (
    <>
      <div className='flex gap-3 items-center justify-center border-b border-black/10 bg-gray-50 px-4 py-2.5 dark:border-gray-900/50 dark:bg-gray-700'>
        {/* Provider Dropdown Chip */}
        <DropdownChip<ProviderKey>
          label={t('provider')}
          value={config.provider}
          options={providerOptions}
          onChange={handleProviderChange}
        />

        {/* Model Dropdown Chip */}
        <DropdownChip
          label={t('model')}
          value={config.modelConfig.model}
          options={modelOptions}
          onChange={handleModelChange}
        />

        {/* Advanced Settings Button */}
        <button
          type="button"
          onClick={() => setIsModalOpen(true)}
          className="ml-auto flex items-center gap-2 px-3 py-1.5 text-sm rounded-md text-gray-600 dark:text-gray-400 hover:bg-gray-300/30 dark:hover:bg-gray-900/30 transition-colors duration-200"
        >
          <SettingIcon className="w-4 h-4" />
          <span className="hidden sm:inline">{t('advanced') || 'Advanced'}</span>
        </button>
      </div>
      {isModalOpen && (
        <ConfigMenu
          setIsModalOpen={setIsModalOpen}
          config={config}
          setConfig={setConfig}
        />
      )}
    </>
  ) : null;
});

export default ChatTitle;
